/**
 * "Store/FC" page - view Building level metrics
 */

import React, { useRef, useEffect, useState, useCallback } from "react"
import PropTypes from "prop-types"
import Column from "@amzn/meridian/column"
import Divider from "@amzn/meridian/divider"
import Loader from "@amzn/meridian/loader"
import Row from "@amzn/meridian/row"
import Heading from "@amzn/meridian/heading"
import PageLayout from "../components/app/page-layout"
import EOSSnapshotTable from "../components/eos-snapshot/eos-snapshot-table"
import CurrentSnapshotTable from "../components/current-nyr-snapshot/current-nyr-snapshot-table"
import usePageLayoutContentRectangle from "../hooks/use-page-layout-content-rectangle"

// API
import { getShipmentSummaries, getNYRSnapshots } from "../graphql/queries";

// Helpers
import { getPaginatedAPICall } from "../utilities/get-paginated-api-call";
import { getWarehouseAttributes } from "../utilities/get-warehouse-attributes";
import { convertUnixDate } from "../utilities/convert-unix";
import { getStartOfDay } from "../utilities/get-start-of-day";
import { calculateCurrentNYR } from "../utilities/calculate-current-nyr";
import { parseShipments } from "../utilities/parse-shipments";


const getTableWidth = wrapperElement => {
  try {
    return wrapperElement.querySelector("table").clientWidth
  } catch (_) {
    return undefined
  }
}

const StoreFCScreen = props => {
 
  const { warehouseParams } = props
  
  const warehouseIds = warehouseParams.map(({ warehouse_id }) => (warehouse_id)).sort(function (a, b) { return a.localeCompare(b);})
  const [currentNYRSnapshot, setCurrentNYRSnapshot] = useState();
  const [eosSnapshots, setEOSSnapshots] = useState();
  const { width: contentWidth = 1200 } = usePageLayoutContentRectangle()
  const tableWrapperRef = useRef();

  const getEOSSnapshots = useCallback(async (warehouses) => {
      let params = {};
      for (const warehouse of warehouses) {
        const warehouseId = warehouse.warehouse_id;
        const timeZone = warehouse.time_zone;
        params[warehouseId] = {warehouseId: warehouseId, minSnapshotTime: getStartOfDay(timeZone, -14)};
      };
      const data = await getPaginatedAPICall(params, getNYRSnapshots, "getNYRSnapshots")
      if (data) {
        setEOSSnapshots(data);
      }
  }, [setEOSSnapshots]);

  const getWarehouseShipmentSummaries = useCallback(async (warehouses) => {
      let params = {};
      for (const warehouse of warehouses) {
        const warehouseId = warehouse.warehouse_id;
        const timeZone = warehouse.time_zone;
        params[warehouseId] = {
          warehouseId: warehouseId,
          minAppointmentTime: getStartOfDay(timeZone, -7),
          maxAppointmentTime: getStartOfDay(timeZone, 1)
        };
      };
      const data = await getPaginatedAPICall(params, getShipmentSummaries, "getShipmentSummaries")
      if (data) {
        const { parsedDockReceived } = parseShipments(data);
        // Dynamically derive temp zones from actual backend data — no hardcoding
        // Sort by units descending so highest NYR appears at the top
        const currentNYR = calculateCurrentNYR(parsedDockReceived)
          .sort((a, b) => {
            const siteCompare = (a.warehouseId || "").localeCompare(b.warehouseId || "");
            if (siteCompare !== 0) return siteCompare;
            return b.units - a.units;
          });
        setCurrentNYRSnapshot(currentNYR);
      };
    }, [setCurrentNYRSnapshot]);

  useEffect(() => {
    getWarehouseShipmentSummaries(warehouseParams);
    getEOSSnapshots(warehouseParams);
  }, [warehouseParams, getWarehouseShipmentSummaries, getEOSSnapshots]);

  // Will probably have to sort incoming data based on timestamp first
  let eosTableHeaders = {};
  // Dynamically derive temp zones from the actual nyrnysSnapshot keys in the data
  let eosTempZones = [];
  if (eosSnapshots) {
    eosSnapshots.map(snapshot => {
      const warehouseId = snapshot.warehouseId;
      const { time_zone } = getWarehouseAttributes(warehouseId, warehouseParams);
      const snapshotDate = convertUnixDate(snapshot.snapshotTime, time_zone);
      const hour = snapshot.warehouseHour;
      snapshot.date = snapshotDate;
      eosTableHeaders[snapshotDate] ? eosTableHeaders[snapshotDate].push(hour) : eosTableHeaders[snapshotDate] = [hour]
      eosTableHeaders[snapshotDate] = [...new Set(eosTableHeaders[snapshotDate])]
      eosTableHeaders[snapshotDate].sort((a,b) => a - b);
      // Collect temp zones from actual snapshot data
      if (snapshot.nyrnysSnapshot) {
        const tempZoneMap = JSON.parse(snapshot.nyrnysSnapshot);
        Object.keys(tempZoneMap).forEach(tz => {
          if (!tz.includes('_') && !eosTempZones.includes(tz)) {
            eosTempZones.push(tz);
          }
        });
      }
      return null
    })
    eosTempZones.sort();
  };

  const tableWidth = getTableWidth(tableWrapperRef.current)
  const isSticky = contentWidth < tableWidth

  return (
    <PageLayout
      title={""}
      loading={!eosSnapshots && !currentNYRSnapshot}
      width={eosSnapshots ? "inherit" : undefined}
      horizontallySticky={isSticky}
      maxWidth={isSticky ? "none" :  undefined}
      alignmentHorizontal={isSticky ? "start" : "center"}
    >
        <Column spacing="600">
          <Row
            spacingInset={isSticky ? "none 500 none none" : undefined}
            ref={tableWrapperRef}
          >
            <Column>
              <Heading level={1} type="h200">End of Shift NYR Snapshot</Heading>
              <EOSSnapshotTable
                eosSnapshots={eosSnapshots}
                tableHeaders={eosTableHeaders}
                tempZones={eosTempZones}
                warehouseIds={warehouseIds}
              />
            </Column>
            </Row>
          <Row><Divider/></Row>
            <Row>
            <Column>
              <Heading level={1} type="h200">NYR/NYS by temp zone and weighted average dwell days</Heading>
              {currentNYRSnapshot ?
                <CurrentSnapshotTable
                  currentNYRSnapshot={currentNYRSnapshot}
                />
                : <Loader/>
              }
            </Column>
          </Row>
        </Column>
    </PageLayout>
  )
}

StoreFCScreen.propTypes = {
  warehouseParams: PropTypes.array.isRequired
}

export default StoreFCScreen